import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./play.css";
import CornerConfetti from "../../components/cornerConfetti";
import { getRuntimeSession } from "../../functions/getRuntimeSession";
import { isProfane } from "../../functions/profanity";
import { computePoints, formatSec } from "../../functions/scoring";
import {
  liveId,
  subscribeLive,
  getLiveOnce,
  joinParticipant,
  heartbeat,
  getMyQuizResponse,
  submitQuizAnswer,
  getMyVote,
  submitVote,
  submitWord,
  subscribeWords,
  submitPin,
  subscribePins,
  subscribeTopParticipants,
  subscribeMyParticipant,
  getParticipantRank,
  getMyQuizResponses,
} from "../../functions/liveSession";

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem("userData") || sessionStorage.getItem("userData") || "null");
  } catch {
    return null;
  }
}

// Decorated question heading — colored triangle flourishes either side,
// matching the presenter screen's treatment.
function DecoHeading({ className = "", children }) {
  return (
    <h1 className={`pl-deco-title ${className}`}>
      <span className="pl-deco-tri pl-deco-tri-l">
        <i className="fa-solid fa-play" style={{ color: "#f2a93b" }}></i>
        <i className="fa-solid fa-play" style={{ color: "#8b5cf6" }}></i>
      </span>
      {children}
      <span className="pl-deco-tri pl-deco-tri-r">
        <i className="fa-solid fa-play" style={{ color: "#d23b78" }}></i>
        <i className="fa-solid fa-play" style={{ color: "#f2a93b" }}></i>
      </span>
    </h1>
  );
}

// 3-2-1 countdown number — color-cycled per digit + a white-bordered
// particle burst on each tick, matching the presenter screen's treatment.
function CountdownNumber({ n }) {
  return (
    <div className={`pl-countdown-num pl-countdown-num-${n}`} key={n}>
      {n}
      <span className="pl-count-burst">
        {Array.from({ length: 8 }).map((_, i) => (
          <span key={i} className="pl-count-particle" style={{ "--i": i }} />
        ))}
      </span>
    </div>
  );
}

function useCountdown(endsAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    // Refresh immediately when the deadline changes (not just on the next
    // 250ms tick) — otherwise the first render after a new endsAt arrives
    // can use a `now` that's up to 250ms stale, which rounds a clean 30.00s
    // timer up to displaying "31" via Math.ceil for that first render.
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [endsAt]);
  if (!endsAt) return null;
  return Math.max(0, Math.ceil((endsAt - now) / 1000));
}

// Elapsed time since the question actually opened, measured against the
// server-synced questionEndsAt (the same timestamp every client's countdown
// is already ticking against) rather than a client-local mount timestamp.
// Live-mode questions open on the PRESENTER's clock (after its own local
// 3-2-1 countdown finishes), then propagate to participants via Firestore —
// any delay in that round trip would otherwise get silently baked into
// "time to answer" if measured from when this participant's component
// happened to mount. Falls back to the local ref for self-paced, which has
// no such round trip.
function elapsedSinceQuestionOpen(questionEndsAt, timerSeconds, fallbackShownAt) {
  if (questionEndsAt && timerSeconds) {
    return Math.max(0, timerSeconds * 1000 - (questionEndsAt - Date.now()));
  }
  return Date.now() - fallbackShownAt;
}

const Play = () => {
  const userData = useMemo(() => readUserData(), []);
  const token = userData ? `${userData.sessionId}&${userData.organizationId}` : null;

  const [runtime, setRuntime] = useState(null);
  const [themename, setThemename] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const id = themename ? liveId(userData.organizationId, userData.sessionId, themename) : null;

  // Cache the ORIGINAL deadline for the current live-mode question the
  // first time we see it. Once a question opens, this participant's own
  // countdown runs to completion on its own clock from here — completely
  // independent of whether the presenter later disconnects. pauseQuestion()
  // nulls live.questionEndsAt in Firestore the instant the presenter's tab
  // closes; deriving `remaining` directly from that value every render (the
  // old approach) meant the presenter's mere absence collapsed `remaining`
  // to null, which flipped `timerActive` to false and instantly locked
  // every participant out mid-question — even with real time still on the
  // clock. The earlier displayRemaining/lastRemainingRef freeze only ever
  // patched the displayed NUMBER, not this underlying lockout, so the
  // button was disabled under a frozen-looking timer. This fixes the real
  // thing: the participant's own locked-in deadline, so both the countdown
  // AND the ability to answer keep running smoothly with no presenter.
  const questionKeyRef = useRef(null);
  const cachedEndsAtRef = useRef(null);
  const questionKey = live ? `${live.currentIndex ?? 0}_${live.currentQuestionIndex ?? 0}` : null;
  if (questionKey !== questionKeyRef.current) {
    questionKeyRef.current = questionKey;
    cachedEndsAtRef.current = null;
  }
  if (live?.phase === "question" && live?.questionEndsAt && cachedEndsAtRef.current == null) {
    cachedEndsAtRef.current = live.questionEndsAt;
  }

  const remaining = useCountdown(cachedEndsAtRef.current);

  // Same reasoning, scoped only to the 3-2-1 pre-question countdown: cache
  // countdownEndsAt the moment it's first seen so the local 3-2-1 always
  // ticks to completion on its own clock, instead of depending on this
  // listener staying perfectly in sync for the whole (very short, 3s)
  // window. Resets whenever we're not in "countdown" phase so the next
  // question's countdown starts fresh.
  const cachedCountdownEndsAtRef = useRef(null);
  if (live?.phase !== "countdown") {
    cachedCountdownEndsAtRef.current = null;
  } else if (live?.countdownEndsAt && cachedCountdownEndsAtRef.current == null) {
    cachedCountdownEndsAtRef.current = live.countdownEndsAt;
  }
  const countdownLeft = useCountdown(cachedCountdownEndsAtRef.current);

  // <USER> (the wrapper every public route, including /play, renders inside
  // — see pages/dddUI/user.jsx) already fetches /fetchThemeData once on
  // mount for site-wide theming. Read that instead of firing our own
  // identical second request — was literally the same endpoint, same
  // payload, same response, called twice on every single /play load.
  const { status: themeStatus, data: themeReduxData } = useSelector((s) => s.theme);
  const themeNameFromRedux = themeReduxData?.themename || themeReduxData?.themeName || null;

  // Resolve selected theme, load runtime, join, subscribe.
  useEffect(() => {
    if (!userData || !token) {
      setError("No participant session. Please log in again.");
      setLoading(false);
      return;
    }
    // <USER>'s fetch hasn't resolved yet — wait for it rather than firing
    // our own. Re-runs when themeStatus/themeNameFromRedux change, so this
    // picks up as soon as that fetch completes.
    if (themeStatus === "idle" || themeStatus === "loading") return undefined;
    if (themeStatus === "failed" || !themeNameFromRedux) {
      setError("No active session found.");
      setLoading(false);
      return undefined;
    }
    let unsub = () => {};
    (async () => {
      try {
        setThemename(themeNameFromRedux);

        const data = await getRuntimeSession({ token, currentTheme: themeNameFromRedux });
        setRuntime(data);

        const lid = liveId(userData.organizationId, userData.sessionId, themeNameFromRedux);
        await joinParticipant(lid, userData.userId, userData.name);
        unsub = subscribeLive(lid, (d) => setLive(d));
      } catch (e) {
        setError(e.message || "Failed to join session.");
      } finally {
        setLoading(false);
      }
    })();
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, themeStatus, themeNameFromRedux]);

  // Heartbeat presence.
  useEffect(() => {
    if (!id) return;
    const t = setInterval(() => heartbeat(id, userData.userId).catch(() => {}), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // No participant-side fallback writes to the live control doc — that
  // responsibility belongs entirely to the presenter (see presenter.jsx's
  // localStorage-backed reconnect logic). Participants only ever read
  // live.questionEndsAt/countdownEndsAt — questionEndsAt is cached locally
  // the moment a question opens (see cachedEndsAtRef above) specifically so
  // the presenter disconnecting and Firestore nulling that value can't cut
  // a participant's own countdown short.

  // iOS Safari aggressively suspends a backgrounded tab's network activity
  // (screen lock, app switch, notification), which can leave the Firestore
  // onSnapshot listener's underlying connection stale for a while after the
  // tab is foregrounded again — the SDK is supposed to auto-reconnect, but
  // iOS's background throttling is a known source of it taking much longer
  // than expected, or not happening promptly at all. Symptom: the question
  // never appears, the countdown looks frozen, even though the presenter
  // has already moved everyone else on. This is a read-only reconciliation
  // (not a write to the control doc, per the no-participant-writes rule
  // above) — just force a fresh one-time fetch the moment the tab becomes
  // visible again, so the UI self-heals instead of waiting indefinitely on
  // a possibly-stuck listener.
  useEffect(() => {
    if (!id) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      getLiveOnce(id)
        .then((d) => setLive(d))
        .catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [id]);

  // Short-lived backup poll, active ONLY during the 3-2-1 countdown phase.
  // The countdown is just 3 seconds total — on iOS specifically, the
  // onSnapshot listener's connection can be slow/throttled even while the
  // tab stays foregrounded the whole time (not just when backgrounded,
  // which the visibilitychange fix above already covers), so relying on it
  // alone for such a short window is what produces "stuck at 1": the local
  // countdown correctly reaches 0, but phase never visibly advances to
  // "question" because the listener just hasn't delivered that write yet.
  // Polling getLiveOnce() every ~600ms here is a cheap, bounded safety net
  // that catches the phase transition promptly regardless of why the live
  // listener is lagging — it only ever runs during this one short window,
  // not for the rest of the session.
  useEffect(() => {
    if (!id || live?.phase !== "countdown") return undefined;
    const t = setInterval(() => {
      // Only apply a real result — a transient null read shouldn't wipe
      // out the live doc we already know exists.
      getLiveOnce(id).then((d) => d && setLive(d)).catch(() => {});
    }, 600);
    return () => clearInterval(t);
  }, [id, live?.phase]);

  if (loading) {
    return (
      <div className="pl-wrap pl-center">
        <div className="pl-spinner"></div>
        <p>Joining session…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="pl-wrap pl-center">
        <p className="pl-error">{error}</p>
      </div>
    );
  }

  const settings = runtime.settings;

  // Self-Paced: fully independent of the presenter/live doc. Participant
  // drives their own pace through quiz questions with per-question timers.
  if (settings.mode === "selfpaced") {
    return <SelfPacedQuiz id={id} runtime={runtime} userData={userData} />;
  }

  const activities = runtime.activities || [];
  const status = live?.status || "waiting";
  const phase = live?.phase || "lobby";
  const idx = live?.currentIndex ?? 0;
  const qIdx = live?.currentQuestionIndex ?? 0;
  const activity = activities[idx] || null;

  // Locked unless the question is actively open with time remaining.
  const timerActive = status === "running" && phase === "question" && remaining > 0;

  // Waiting screen (before start, or lobby)
  if (status === "waiting" || phase === "lobby") {
    return (
      <div className="pl-wrap pl-center">
        <div className="pl-card pl-waiting">
          <div className="pl-pill">YOU'RE IN</div>
          <h1 className="pl-title">{settings.title || themename}</h1>
          {settings.description && <p className="pl-desc">{settings.description}</p>}
          <div className="pl-waiting-hint">
            <div className="pl-dots"><span></span><span></span><span></span></div>
            Waiting for the host to start…
          </div>
          <div className="pl-you">You: <strong>{userData.name}</strong></div>
        </div>
      </div>
    );
  }

  // 3-2-1 countdown before a question
  if (phase === "countdown") {
    const n = countdownLeft == null ? 3 : Math.max(1, countdownLeft);
    return (
      <div className="pl-wrap pl-center">
        <div className="pl-countdown">
          <CountdownNumber n={n} />
          <div className="pl-countdown-label">Get ready…</div>
        </div>
      </div>
    );
  }

  if (status === "ended" || phase === "finalleaderboard") {
    return (
      <div className="pl-wrap pl-center">
        <div className="pl-card pl-center">
          <div className="pl-pill">SESSION COMPLETE</div>
          <h1 className="pl-title">Thanks for playing!</h1>
          <p className="pl-desc">The host has ended the session.</p>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="pl-wrap pl-center">
        <div className="pl-card"><p>Waiting for the host…</p></div>
      </div>
    );
  }

  // Per-question timer / Time's up — shown ABOVE the question.
  const timerBar =
    phase === "timesup" ? (
      <div className="pl-qtimer pl-qtimer-up">⏱ Time's up</div>
    ) : phase === "question" ? (
      <div className="pl-qtimer">
        <i className="fa-regular fa-clock"></i> {remaining == null ? "--" : remaining}s
      </div>
    ) : null;

  return (
    <div className="pl-wrap">
      <div className="pl-body">
        <div className="pl-q-col">
          {timerBar}
          {activity.type === "quiz" && (
            <QuizPlay id={id} activity={activity} qIdx={qIdx} phase={phase === "timesup" ? "question" : phase} settings={settings} userData={userData} locked={!timerActive} questionEndsAt={cachedEndsAtRef.current} />
          )}
          {activity.type === "poll" && (
            <PollPlay id={id} activity={activity} qIdx={qIdx} userData={userData} locked={!timerActive} />
          )}
          {activity.type === "wordcloud" && (
            <WordPlay id={id} activity={activity} qIdx={qIdx} settings={settings} userData={userData} locked={!timerActive} />
          )}
          {activity.type === "pindrop" && (
            <PinPlay id={id} activity={activity} qIdx={qIdx} settings={settings} userData={userData} locked={!timerActive} questionEndsAt={cachedEndsAtRef.current} />
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Self-Paced: independent, timer-driven flow (no presenter needed) --
// Flattens every quiz question across all quiz activities into one ordered
// list and drives itself: 3-2-1 -> question (own timer) -> time's up/lock ->
// auto-advance -> ... -> final (leaderboard per admin setting).
function SelfPacedQuiz({ id, runtime, userData }) {
  const activities = (runtime.activities || []).filter((a) => a.type === "quiz");
  const flat = [];
  activities.forEach((a) => {
    (a.questions || []).forEach((q) => {
      flat.push({ activityId: a.id, timerSeconds: a.timer_seconds || 30, q });
    });
  });

  const [stage, setStage] = useState("resuming"); // resuming | countdown | question | timesup | done
  const [qPos, setQPos] = useState(0);
  const [countdownEndsAt, setCountdownEndsAt] = useState(null);
  const [questionEndsAt, setQuestionEndsAt] = useState(null);
  const [myResp, setMyResp] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const shownAt = useRef(Date.now());
  // Blocks a double-tapped Submit from firing twice before `myResp` (set
  // only after the submit call resolves) has re-rendered to disable it —
  // set synchronously, not via state, so the very next click sees it.
  const submittingRef = useRef(false);
  // Separate from submittingRef above: this one IS state, so it drives an
  // immediate "Submitting…" UI the instant the button is clicked. Without
  // it, nothing visibly changes until submitQuizAnswer's Firestore
  // transaction round-trip resolves (a real network round trip even when
  // everything else is running locally — a transaction can't commit
  // without a server read-then-write), which reads as "did this even
  // register?" during that gap.
  const [submitting, setSubmitting] = useState(false);

  const countdownLeft = useCountdown(stage === "countdown" ? countdownEndsAt : null);
  const remaining = useCountdown(stage === "question" ? questionEndsAt : null);

  const current = flat[qPos];
  const settings = runtime.settings;

  // On mount, find out how far this participant already got — otherwise a
  // page refresh always restarted the quiz from question 1 with a fresh
  // timer, even for questions already answered (and bounced them back to
  // the countdown instead of the finished/leaderboard screen if they'd
  // already completed it).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (flat.length === 0) {
        setStage("done");
        return;
      }
      const responses = await getMyQuizResponses(id, userData.userId);
      if (cancelled) return;
      const resumeAt = flat.findIndex((f) => !responses[`${f.activityId}_${f.q.id}`]);
      if (resumeAt === -1) {
        setStage("done");
      } else {
        setQPos(resumeAt);
        setCountdownEndsAt(Date.now() + 3000);
        setStage("countdown");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userData.userId]);

  // Countdown finished -> open the question with its own timer.
  useEffect(() => {
    if (stage !== "countdown" || countdownLeft == null || countdownLeft > 0) return;
    if (!current) {
      setStage("done");
      return;
    }
    setSelected(null);
    setMyResp(null);
    setShowConfetti(false);
    shownAt.current = Date.now();
    submittingRef.current = false;
    setSubmitting(false);
    setQuestionEndsAt(Date.now() + current.timerSeconds * 1000);
    setStage("question");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdownLeft, stage]);

  // Question timer elapsed -> time's up (lock).
  useEffect(() => {
    if (stage !== "question" || remaining == null || remaining > 0) return;
    setStage("timesup");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, stage]);

  // Time's up -> auto-advance shortly. Kept in its own effect (deps: [stage]
  // only) so the setStage above doesn't retrigger this effect and clear the
  // timeout it just scheduled before it fires.
  useEffect(() => {
    if (stage !== "timesup") return;
    const t = setTimeout(() => advance(), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const advance = () => {
    const next = qPos + 1;
    if (next >= flat.length) {
      setStage("done");
      return;
    }
    setQPos(next);
    setCountdownEndsAt(Date.now() + 3000);
    setStage("countdown");
  };

  const submit = async () => {
    if (selected == null || myResp || !current || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const correct = selected === current.q.answer_index - 1;
    const timeMs = Date.now() - shownAt.current;
    const points = computePoints({
      correct,
      timeMs,
      timerSeconds: current.timerSeconds,
      scoringMode: settings.scoring_mode,
      pointValue: settings.point_value,
    });
    try {
      const saved = await submitQuizAnswer(id, {
        activityId: current.activityId,
        qIdx: current.q.id, // unique per question across activities
        userId: userData.userId,
        name: userData.name,
        optionIndex: selected,
        correct,
        points,
        timeMs,
      });
      setMyResp(saved);
      if (saved.correct) setShowConfetti(true);
      // brief pause so the participant sees locked/correct state, then advance
      setTimeout(() => advance(), 900);
    } catch (e) {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (stage === "resuming") {
    return (
      <div className="pl-wrap pl-center">
        <div className="pl-spinner"></div>
      </div>
    );
  }

  if (flat.length === 0) {
    return (
      <div className="pl-wrap pl-center">
        <div className="pl-card"><p>This session has no quiz questions yet.</p></div>
      </div>
    );
  }

  if (stage === "done") {
    return <SelfPacedDone id={id} userData={userData} settings={settings} />;
  }

  if (stage === "countdown") {
    const n = countdownLeft == null ? 3 : Math.max(1, countdownLeft);
    return (
      <div className="pl-wrap pl-center">
        <div className="pl-countdown">
          <CountdownNumber n={n} />
          <div className="pl-countdown-label">Get ready…</div>
        </div>
      </div>
    );
  }

  const q = current.q;
  const timerBar =
    stage === "timesup" ? (
      <div className="pl-qtimer pl-qtimer-up">⏱ Time's up</div>
    ) : (
      <div className="pl-qtimer">
        <i className="fa-regular fa-clock"></i> {remaining == null ? "--" : remaining}s
      </div>
    );

  return (
    <div className="pl-wrap">
      <div className="pl-body">
        <div className="pl-q-col">
          {timerBar}
          <div className={`pl-card ${!myResp && hasMedia(q) ? "pl-has-media" : ""}`}>
            {myResp ? (
              <>
                {showConfetti && <CornerConfetti onDone={() => setShowConfetti(false)} />}
                <div className="pl-pill">{myResp.correct ? "CORRECT" : "LOCKED"}</div>
                <DecoHeading className="pl-q">{q.question_text}</DecoHeading>
                <div className={`pl-result ${myResp.correct ? "pl-correct" : "pl-incorrect"}`}>
                  {myResp.correct ? "Correct!" : "Incorrect"}
                </div>
                <div className="pl-result-meta">
                  +{myResp.points || 0} points · {formatSec(myResp.timeMs)}s
                </div>
              </>
            ) : (
              <MediaSplit q={q}>
                <div className="pl-pill">
                  {q.format === "truefalse" ? "TRUE / FALSE" : q.format === "mythfact" ? "MYTH / FACT" : "MULTIPLE CHOICE"}
                </div>
                <DecoHeading className="pl-q">{q.question_text}</DecoHeading>
                <div className="pl-options">
                  {q.options.map((o, i) => (
                    <button
                      key={i}
                      className={`pl-option ${selected === i ? "pl-option-sel" : ""}`}
                      onClick={() => stage === "question" && setSelected(i)}
                      disabled={stage !== "question"}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                <button className="pl-submit" onClick={submit} disabled={selected == null || stage !== "question" || submitting}>
                  {submitting ? "Submitting…" : <>Submit Answer <i className="fa-solid fa-circle-chevron-right"></i></>}
                </button>
                {stage === "timesup" && <p className="pl-locked-note">Time's up — moving on…</p>}
              </MediaSplit>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const LEADERBOARD_TOP_N = 5;

function formatLbTime(ms) {
  return `${((ms || 0) / 1000).toFixed(1)}s`;
}

function LeaderboardRow({ rank, p, isMe }) {
  return (
    <div className={`pl-lb-row ${isMe ? "pl-lb-row-me" : ""} ${rank === 1 ? "pl-lb-row-first" : ""}`}>
      <span className="pl-lb-rank">{rank}</span>
      <span className="pl-lb-name">
        {p.name || p.userId}
        {isMe && <small>You</small>}
      </span>
      <span className="pl-lb-points">{p.points || 0} pts</span>
      <span className="pl-lb-time">{formatLbTime(p.totalTimeMs)}</span>
    </div>
  );
}

// Self-paced completion screen — leaderboard shown only if the admin enabled it.
//
// Deliberately does NOT watch the full participants collection: if every
// participant who finishes lands on this screen with showLeaderboard on,
// an unscoped listener here means every single submission anywhere in the
// session re-delivers the entire roster to every other finished participant
// at once (O(participants^2)). Instead: a bounded top-N listener for the
// list, this participant's own single-doc listener for their own score, and
// a server-side count aggregation (not a full download) for their rank.
function SelfPacedDone({ id, userData, settings }) {
  const showLeaderboard = !!settings.selfpaced_leaderboard_visible;
  const [top, setTop] = useState([]);
  const [me, setMe] = useState(null);
  const [aheadCount, setAheadCount] = useState(null); // how many participants outrank me

  const myUserId = String(userData.userId);

  useEffect(() => {
    if (!showLeaderboard) return undefined;
    const unsubTop = subscribeTopParticipants(id, LEADERBOARD_TOP_N, setTop);
    const unsubMe = subscribeMyParticipant(id, myUserId, setMe);
    return () => {
      unsubTop();
      unsubMe();
    };
  }, [id, showLeaderboard, myUserId]);

  // Rank is a periodic count aggregation rather than a live listener — exact
  // real-time rank isn't worth a full-collection watch on a completion screen.
  useEffect(() => {
    if (!showLeaderboard || !me) return undefined;
    let cancelled = false;
    const refresh = () => {
      getParticipantRank(id, me.points || 0).then((count) => {
        if (!cancelled) setAheadCount(count);
      });
    };
    refresh();
    const interval = setInterval(refresh, 8000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id, showLeaderboard, me]);

  const myRank = aheadCount == null ? null : aheadCount;
  const myOutsideTop = myRank != null && myRank >= LEADERBOARD_TOP_N;

  if (!showLeaderboard) {
    return (
      <div className="pl-wrap pl-center">
        <div className="pl-card">
          <div className="pl-pill">QUIZ COMPLETE</div>
          <h1 className="pl-title">Thanks for playing!</h1>
          <p className="pl-desc">Your answers have been submitted.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pl-wrap pl-center">
      <div className="pl-card pl-card-lb">
        <div className="pl-pill">LEADERBOARD</div>
        {top.length === 0 ? (
          <p className="pl-desc">Loading leaderboard…</p>
        ) : (
          <>
            <div className="pl-lb-header">
              <span>Rank</span>
              <span>Name</span>
              <span>Points</span>
              <span>Time</span>
            </div>
            <div className="pl-lb-list">
              {top.map((p, i) => (
                <LeaderboardRow key={p.userId} rank={i + 1} p={p} isMe={String(p.userId) === myUserId} />
              ))}
            </div>
            {myOutsideTop && me && (
              <>
                <div className="pl-lb-sep">···</div>
                <div className="pl-lb-list">
                  <LeaderboardRow rank={myRank + 1} p={me} isMe />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ---- Quiz participant --------------------------------------------------
function QuizPlay({ id, activity, qIdx, phase, settings, userData, locked, questionEndsAt }) {
  const q = activity.questions[qIdx];
  const [selected, setSelected] = useState(null);
  const [myResp, setMyResp] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const shownAt = useRef(Date.now());
  // Blocks a double-tapped Submit from firing twice before `myResp` (set
  // only after the submit call resolves) has re-rendered to disable it —
  // set synchronously, not via state, so the very next click sees it.
  const submittingRef = useRef(false);
  // State counterpart (see SelfPacedQuiz above) so the button shows
  // immediate feedback instead of looking unresponsive during the
  // Firestore transaction round-trip.
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSelected(null);
    setMyResp(null);
    setShowConfetti(false);
    shownAt.current = Date.now();
    submittingRef.current = false;
    setSubmitting(false);
    (async () => {
      const existing = await getMyQuizResponse(id, activity.id, qIdx, userData.userId);
      if (existing) setMyResp(existing);
    })();
  }, [id, activity.id, qIdx, userData.userId]);

  if (!q) return <div className="pl-card">No question.</div>;

  const submit = async () => {
    if (selected == null || myResp || locked || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const correct = selected === q.answer_index - 1;
    const timeMs = elapsedSinceQuestionOpen(questionEndsAt, activity.timer_seconds, shownAt.current);
    const points = computePoints({
      correct,
      timeMs,
      timerSeconds: activity.timer_seconds,
      scoringMode: settings.scoring_mode,
      pointValue: settings.point_value,
    });
    try {
      const saved = await submitQuizAnswer(id, {
        activityId: activity.id,
        qIdx,
        userId: userData.userId,
        name: userData.name,
        optionIndex: selected,
        correct,
        points,
        timeMs,
      });
      setMyResp(saved);
      if (saved.correct) setShowConfetti(true);
    } catch (e) {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // Already answered -> stay locked (no spoilers) until the host reveals
  // the answer on the shared screen; only then show correct/incorrect and
  // points/time on this participant's own device.
  const revealed = phase === "reveal" || phase === "qleaderboard";
  if (myResp) {
    return (
      <div className="pl-card">
        {showConfetti && revealed && <CornerConfetti onDone={() => setShowConfetti(false)} />}
        <div className="pl-pill">{revealed ? (myResp.correct ? "CORRECT" : "INCORRECT") : "LOCKED"}</div>
        <DecoHeading className="pl-q">{q.question_text}</DecoHeading>
        {revealed ? (
          <>
            <div className={`pl-result ${myResp.correct ? "pl-correct" : "pl-incorrect"}`}>
              {myResp.correct ? "Correct!" : "Incorrect"}
            </div>
            <div className="pl-result-meta">
              +{myResp.points || 0} points · {formatSec(myResp.timeMs)}s
            </div>
          </>
        ) : (
          <p className="pl-locked-note">Answer locked. Waiting for the host…</p>
        )}
      </div>
    );
  }

  return (
    <div className={`pl-card ${hasMedia(q) ? "pl-has-media" : ""}`}>
      <MediaSplit q={q}>
        <div className="pl-pill">
          {q.format === "truefalse" ? "TRUE / FALSE" : q.format === "mythfact" ? "MYTH / FACT" : "MULTIPLE CHOICE"}
        </div>
        <DecoHeading className="pl-q">{q.question_text}</DecoHeading>
        <div className="pl-options">
          {q.options.map((o, i) => (
            <button
              key={i}
              className={`pl-option ${selected === i ? "pl-option-sel" : ""}`}
              onClick={() => !locked && setSelected(i)}
              disabled={locked}
            >
              {o}
            </button>
          ))}
        </div>
        <button className="pl-submit" onClick={submit} disabled={selected == null || locked || submitting}>
          {submitting ? "Submitting…" : <>Submit Answer <i className="fa-solid fa-circle-chevron-right"></i></>}
        </button>
        {locked && <p className="pl-locked-note">Time's up — submissions closed.</p>}
      </MediaSplit>
    </div>
  );
}

function QMedia({ q }) {
  if (!q.media_url || q.media_type === "none") return null;
  const src = process.env.REACT_APP_S3_PATH + q.media_url;
  if (q.media_type === "image") return <img className="pl-media" src={src} alt="" />;
  if (q.media_type === "video")
    return (
      <video
        className="pl-media"
        src={src}
        controls
        playsInline
        webkit-playsinline="true"
      />
    );
  if (q.media_type === "audio") return <audio className="pl-media" src={src} controls />;
  return null;
}

function hasMedia(q) {
  return Boolean(q.media_url) && q.media_type !== "none";
}

// On desktop, media sits to the left and the question/options sit to the
// right so wide cards (image/video + several options) don't need to scroll.
function MediaSplit({ q, children }) {
  if (!hasMedia(q)) return <>{children}</>;
  return (
    <div className="pl-media-layout">
      <div className="pl-media-pane">
        <QMedia q={q} />
      </div>
      <div className="pl-content-pane">{children}</div>
    </div>
  );
}

// ---- Poll participant --------------------------------------------------
function PollPlay({ id, activity, qIdx, userData, locked }) {
  const q = activity.questions[qIdx];
  const [selected, setSelected] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSelected(null);
    setMyVote(null);
    submittingRef.current = false;
    setSubmitting(false);
    (async () => {
      const v = await getMyVote(id, activity.id, qIdx, userData.userId);
      if (v) setMyVote(v);
    })();
  }, [id, activity.id, qIdx, userData.userId]);

  if (!q) return <div className="pl-card">No question.</div>;

  const submit = async () => {
    if (selected == null || myVote || locked || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const saved = await submitVote(id, {
        activityId: activity.id,
        qIdx,
        userId: userData.userId,
        name: userData.name,
        optionIndex: selected,
      });
      setMyVote(saved);
    } catch (e) {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (myVote) {
    return (
      <div className="pl-card">
        <div className="pl-pill">VOTE LOCKED</div>
        <DecoHeading className="pl-q">{q.question_text}</DecoHeading>
        <div className="pl-result pl-correct">You voted: {q.options[myVote.optionIndex]}</div>
        <p className="pl-locked-note">Waiting for the host…</p>
      </div>
    );
  }

  return (
    <div className={`pl-card ${hasMedia(q) ? "pl-has-media" : ""}`}>
      <MediaSplit q={q}>
        <div className="pl-pill">LIVE POLL</div>
        <DecoHeading className="pl-q">{q.question_text}</DecoHeading>
        <div className="pl-options">
          {q.options.map((o, i) => (
            <button
              key={i}
              className={`pl-option ${selected === i ? "pl-option-sel" : ""}`}
              onClick={() => !locked && setSelected(i)}
              disabled={locked}
            >
              {o}
            </button>
          ))}
        </div>
        <button className="pl-submit" onClick={submit} disabled={selected == null || locked || submitting}>
          {submitting ? "Submitting…" : <>Submit Vote <i className="fa-solid fa-circle-chevron-right"></i></>}
        </button>
      </MediaSplit>
    </div>
  );
}

const WORDCLOUD_MAX_CHARS = 15;

// ---- Word cloud participant --------------------------------------------
function WordPlay({ id, activity, qIdx, settings, userData, locked }) {
  const q = activity.questions[qIdx];
  const [text, setText] = useState("");
  const [myCount, setMyCount] = useState(0);
  const limit = settings.wordcloud_limit || 3;

  useEffect(() => {
    setText("");
    const unsub = subscribeWords(id, activity.id, qIdx, (words) => {
      setMyCount(words.filter((w) => w.userId === String(userData.userId) && !w.removed).length);
    });
    return () => unsub();
  }, [id, activity.id, qIdx, userData.userId]);

  if (!q) return <div className="pl-card">No prompt.</div>;

  const add = async () => {
    const t = text.trim().slice(0, WORDCLOUD_MAX_CHARS);
    if (!t || locked) return;
    if (myCount >= limit) {
      // allowEnterKey: false — when add() is triggered from the Enter
      // keydown handler below, the SAME physical key-press's lingering
      // keyup event reaches this freshly-mounted dialog, and SweetAlert2's
      // default behavior treats Enter as "confirm/dismiss." Without this,
      // the popup opens and immediately self-closes within one keystroke.
      Swal.fire({ title: "", text: `You've reached the limit of ${limit} responses.`, icon: "info", allowEnterKey: false });
      return;
    }
    if (isProfane(t)) {
      Swal.fire({ title: "", text: "That response was blocked by the profanity filter.", icon: "warning", allowEnterKey: false });
      setText("");
      return;
    }
    await submitWord(id, {
      activityId: activity.id,
      qIdx,
      userId: userData.userId,
      name: userData.name,
      text: t,
    });
    setText("");
  };

  return (
    <div className="pl-card">
      <div className="pl-pill">LIVE ACTIVITY</div>
      <DecoHeading className="pl-q">{q.prompt}</DecoHeading>
      <label className="pl-field-label">Your response</label>
      <input
        className="pl-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={WORDCLOUD_MAX_CHARS}
        placeholder="Type a word…"
        disabled={locked || myCount >= limit}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault(); // stop this same key-press from reaching the dialog add() is about to open
          add();
        }}
      />
      <button className="pl-submit" onClick={add} disabled={!text.trim() || locked || myCount >= limit}>
        Add to Cloud
      </button>
      <div className="pl-wc-count">{myCount} / {limit} submitted</div>
    </div>
  );
}

// ---- Pin drop participant ----------------------------------------------
function PinPlay({ id, activity, qIdx, settings, userData, locked, questionEndsAt }) {
  const q = activity.questions[qIdx];
  const [pending, setPending] = useState(null); // {x,y} normalized, not yet confirmed
  const [myPins, setMyPins] = useState([]); // confirmed pins (this participant, this question)
  const [showConfetti, setShowConfetti] = useState(false);
  const stageRef = useRef(null);
  const shownAt = useRef(Date.now());
  // Blocks a double-tapped Confirm from firing twice before `pending` (only
  // cleared after the submit call resolves) has re-rendered to disable it —
  // set synchronously, not via state, so the very next click sees it.
  const submittingRef = useRef(false);
  const limit = settings.pindrop_limit || 1;

  useEffect(() => {
    setPending(null);
    setShowConfetti(false);
    shownAt.current = Date.now();
    submittingRef.current = false;
    const unsub = subscribePins(id, activity.id, qIdx, (pins) => {
      setMyPins(pins.filter((p) => p.userId === String(userData.userId)));
    });
    return () => unsub();
  }, [id, activity.id, qIdx, userData.userId]);

  if (!q) return <div className="pl-card">No pin drop.</div>;

  const pct = (v) => `${v * 100}%`;
  const myCount = myPins.length;

  // Participants can place anywhere on the image — the drop zones are not
  // shown and not enforced at placement time; correctness is judged silently
  // when the pin is confirmed.
  const place = (e) => {
    if (locked || myCount >= limit) return;
    const rect = stageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setPending({ x, y });
  };

  const confirm = async () => {
    if (!pending || locked || submittingRef.current) return;
    submittingRef.current = true;
    const timeMs = elapsedSinceQuestionOpen(questionEndsAt, activity.timer_seconds, shownAt.current);
    try {
      const saved = await submitPin(id, {
        activityId: activity.id,
        qIdx,
        userId: userData.userId,
        name: userData.name,
        x: pending.x,
        y: pending.y,
        polygons: q.polygons,
        pointValue: settings.point_value,
        scoringMode: settings.scoring_mode,
        timerSeconds: activity.timer_seconds,
        timeMs,
      });
      setPending(null);
      if (saved.correct) setShowConfetti(true);
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <div className="pl-card">
      {showConfetti && <CornerConfetti onDone={() => setShowConfetti(false)} />}
      <div className="pl-pill">PIN DROP</div>
      <DecoHeading className="pl-q">{q.prompt}</DecoHeading>
      <div className="pl-pin-stage" ref={stageRef} onClick={place}>
        {q.image_url && <img src={process.env.REACT_APP_S3_PATH + q.image_url} alt="" draggable={false} />}
        {/* Drop zones are intentionally NOT rendered — participants can't see them. */}
        {myPins.map((p) => (
          <div
            key={p.docId || `${p.x}-${p.y}`}
            className={`pl-pin pl-pin-confirmed pl-pin-pop ${p.correct ? "pl-pin-correct" : "pl-pin-wrong"}`}
            style={{ left: pct(p.x), top: pct(p.y) }}
          >
            <i className="fa-solid fa-location-dot"></i>
          </div>
        ))}
        {pending && (
          <div className="pl-pin pl-pin-pending" style={{ left: pct(pending.x), top: pct(pending.y) }}>
            <i className="fa-solid fa-location-dot"></i>
          </div>
        )}
      </div>
      {myCount >= limit ? (
        <p className="pl-locked-note">You've placed all your pins ({limit}).</p>
      ) : (
        <>
          <p className="pl-pin-hint">Tap the image to place your pin, then confirm.</p>
          <button className="pl-submit" onClick={confirm} disabled={!pending || locked}>
            Confirm Pin
          </button>
        </>
      )}
      <div className="pl-wc-count">{myCount} / {limit} pins</div>
    </div>
  );
}

export default Play;
